import { modifier } from 'ember-modifier';

// @ts-ignore
type Positional = [speed?: number]; // velocidad principal (0.1 lento, 1 rápido)

type Named = {
  axis?: 'y' | 'x';
  invert?: boolean; // true = se mueve contra el scroll (efecto clásico)
  factor?: number; // multiplicador fino (por defecto = 1)
  min?: number | null; // clamp inferior en px de translate
  max?: number | null; // clamp superior en px de translate
  disabled?: boolean; // para cortar en mobile o según prefers-reduced-motion
  rootMargin?: string; // margen del root para el intersection observer
  threshold?: number; // umbral de visibilidad (0-1)
  smoothTransition?: boolean; // transición suave al entrar/salir del viewport
  transitionDuration?: number; // duración de la transición en ms
};

export default modifier<HTMLElement, Positional, Named>(function parallaxLayer(
  element,
  [speed = 0.3],
  {
    axis = 'y',
    invert = true,
    factor = 1,
    min = null,
    max = null,
    disabled = false,
    rootMargin = '20% 0px', // Comienza a animar un poco antes de que sea totalmente visible
    threshold = 0,
    smoothTransition = true, // Por defecto habilitamos la transición suave
    transitionDuration = 300, // 300ms de transición
  } = {},
) {
  // Respeta prefers-reduced-motion
  const prefersReduced = window.matchMedia?.(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (prefersReduced || disabled) {
    element.style.transform = '';
    return;
  }

  element.style.willChange = 'transform';

  let isVisible = false;
  let animationId: number | null = null;
  let targetDelta = 0;
  let currentDelta = 0;
  let easing = smoothTransition ? 0.08 : 1; // Factor de suavizado (más bajo = más suave)

  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor;
  };

  const updateTransform = (delta: number) => {
    const transform =
      axis === 'y'
        ? `translate3d(0, ${delta}px, 0)`
        : `translate3d(${delta}px, 0, 0)`;
    element.style.transform = transform;
  };

  const calculateTargetDelta = () => {
    const y = window.scrollY || 0;
    let delta = y * speed * factor * (invert ? -1 : 1);

    if (typeof min === 'number') delta = Math.max(min, delta);
    if (typeof max === 'number') delta = Math.min(max, delta);

    return delta;
  };

  const animate = () => {
    if (!isVisible) return;

    // Calcular la posición objetivo basada en el scroll actual
    targetDelta = calculateTargetDelta();

    // Interpolar suavemente hacia la posición objetivo
    const diff = Math.abs(targetDelta - currentDelta);

    if (diff > 0.1) {
      currentDelta = lerp(currentDelta, targetDelta, easing);
      updateTransform(currentDelta);

      // Continuar la animación
      animationId = requestAnimationFrame(animate);
    } else {
      // Ya llegamos al objetivo, detener la animación
      currentDelta = targetDelta;
      updateTransform(currentDelta);
      animationId = null;
    }
  };

  const startAnimation = () => {
    if (!animationId && isVisible) {
      animationId = requestAnimationFrame(animate);
    }
  };

  const onScroll = () => {
    // Cada vez que hay scroll, activar la animación suave hacia la nueva posición
    startAnimation();
  };

  // Intersection Observer para detectar visibilidad
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const wasVisible = isVisible;
        isVisible = entry.isIntersecting;

        if (isVisible && !wasVisible) {
          // Inicializar valores cuando se hace visible
          targetDelta = calculateTargetDelta();
          currentDelta = targetDelta; // Empezar desde la posición correcta
          updateTransform(currentDelta);
          startAnimation();
        } else if (!isVisible && wasVisible) {
          // Limpiar animaciones cuando sale del viewport
          if (animationId) {
            cancelAnimationFrame(animationId);
            animationId = null;
          }
        }
      });
    },
    {
      rootMargin,
      threshold,
    }
  );

  // Comienza a observar el elemento
  observer.observe(element);

  window.addEventListener('scroll', onScroll, { passive: true });

  // Aplicación inicial solo si ya es visible
  const initialCheck = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible = true;
        targetDelta = calculateTargetDelta();
        currentDelta = targetDelta;
        updateTransform(currentDelta);
      }
      initialCheck.disconnect();
    },
    { rootMargin, threshold }
  );
  initialCheck.observe(element);

  return () => {
    window.removeEventListener('scroll', onScroll);
    observer.disconnect();
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    element.style.willChange = '';
  };
});
